import { useEffect, useMemo } from 'react';
import { Alert, Checkbox, Flex, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';

import {
	dbmToWatts,
	RADIO_PARAMETER_LIMITS,
	RadioBudgetField,
	TRANSMIT_POWER_WATTS_LIMITS,
	wattsToDbm,
} from '../../common/radioParameters';
import { PathlossCatalogResponse, PathlossProfile, RadioParameters } from '../../types/systems';

interface RadioParametersEditorProps {
	value?: RadioParameters;
	catalog: PathlossCatalogResponse;
	onChange: (value?: RadioParameters) => void;
}

const DEFAULT_THRESHOLDS = [0, 5, 12];
const UNIT_SECTION_WIDTH = 52;

const BUDGET_FIELDS: RadioBudgetField[] = [
	'txGainDbi',
	'rxGainDbi',
	'systemLossDb',
	'fadingMarginDb',
	'receiverSensitivityDbm',
	'thresholdDb',
];

const sameNumbers = (left?: number[], right?: number[]) =>
	left?.length === right?.length && left?.every((value, index) => value === right?.[index]);

const profileLabel = (profile: PathlossProfile) =>
	[profile.model, profile.variant, profile.profileId].filter(Boolean).join(' — ');

export const RadioParametersEditor: React.FC<RadioParametersEditorProps> = ({
	value,
	catalog,
	onChange,
}) => {
	const selectedProfile = catalog.profiles.find(
		(profile) => profile.profileId === value?.profileId
	);
	const profileGroups = useMemo(() => {
		const byMap = new Map<string, PathlossProfile[]>();
		for (const profile of catalog.profiles) {
			byMap.set(profile.scenario, [...(byMap.get(profile.scenario) || []), profile]);
		}
		const options = Array.from(byMap.entries()).flatMap(([map, profiles]) =>
			profiles.map((profile) => ({
				value: profile.profileId,
				label: profileLabel(profile),
				group: map,
			}))
		);
		if (value?.profileId && !selectedProfile) {
			options.unshift({
				group: 'Unavailable profile',
				value: value.profileId,
				label: value.profileId,
			});
		}
		return options;
	}, [catalog.profiles, selectedProfile, value?.profileId]);

	useEffect(() => {
		if (!value) return;
		const thresholds = value.linkQualityDisplayThresholdsDb || DEFAULT_THRESHOLDS;
		const frequencyMhz = selectedProfile?.frequencyMhz ?? value.frequencyMhz;
		const antennaHeightM = selectedProfile?.txHeightM ?? value.antennaHeightM;
		if (
			!sameNumbers(value.linkQualityDisplayThresholdsDb, thresholds) ||
			value.frequencyMhz !== frequencyMhz ||
			value.antennaHeightM !== antennaHeightM
		) {
			onChange({
				...value,
				linkQualityDisplayThresholdsDb: thresholds,
				frequencyMhz,
				antennaHeightM,
			});
		}
	}, [onChange, selectedProfile, value]);

	const setField = (field: keyof RadioParameters, fieldValue: unknown) => {
		if (!value) return;
		onChange({ ...value, [field]: fieldValue });
	};

	const setThreshold = (index: number, threshold: number | string) => {
		if (!value || typeof threshold !== 'number' || !Number.isFinite(threshold)) return;
		const thresholds = [...(value.linkQualityDisplayThresholdsDb || DEFAULT_THRESHOLDS)];
		thresholds[index] = threshold;
		onChange({ ...value, linkQualityDisplayThresholdsDb: thresholds });
	};

	const enableRadioParameters = () => {
		onChange({
			profileId: '',
			waveformKey: '',
			linkQualityDisplayThresholdsDb: DEFAULT_THRESHOLDS,
		});
	};

	const unknownProfile = Boolean(value?.profileId && !selectedProfile);

	return (
		<Stack spacing="sm" w="100%">
			<Checkbox
				label="Use pathloss radio parameters"
				checked={Boolean(value)}
				disabled={!value && (!catalog.available || catalog.profiles.length === 0)}
				onChange={(event) =>
					event.currentTarget.checked ? enableRadioParameters() : onChange(undefined)
				}
			/>

			{!catalog.available ? (
				<Alert color="yellow" title="Pathloss catalog unavailable">
					{catalog.reason || 'Profile metadata cannot be downloaded.'} Existing radio
					parameters are preserved.
				</Alert>
			) : catalog.profiles.length === 0 ? (
				<Alert color="yellow" title="No propagation profiles">
					The pathloss database contains no propagation profiles.
				</Alert>
			) : null}

			{value ? (
				<>
					{unknownProfile ? (
						<Alert color="yellow" title="Profile not present in catalog">
							The saved profile &apos;{value.profileId}&apos; is preserved. Select a
							current profile to replace it.
						</Alert>
					) : null}
					<Select
						label="Propagation profile"
						placeholder="Select profile"
						searchable
						data={profileGroups}
						value={value.profileId || null}
						onChange={(profileId) => {
							const profile = catalog.profiles.find(
								(item) => item.profileId === profileId
							);
							onChange({
								...value,
								profileId: profileId || '',
								frequencyMhz: profile?.frequencyMhz,
								antennaHeightM: profile?.txHeightM,
							});
						}}
					/>
					<Flex gap="sm" direction={{ base: 'column', sm: 'row' }}>
						<NumberInput
							label="Frequency"
							description="From profile manifest"
							rightSection={<Text size="xs">MHz</Text>}
							rightSectionWidth={UNIT_SECTION_WIDTH}
							value={value.frequencyMhz}
							precision={3}
							removeTrailingZeros
							disabled
							w="100%"
						/>
						<NumberInput
							label="Transmit antenna height"
							description="From profile manifest"
							rightSection={<Text size="xs">m</Text>}
							rightSectionWidth={UNIT_SECTION_WIDTH}
							value={value.antennaHeightM}
							precision={2}
							removeTrailingZeros
							disabled
							w="100%"
						/>
					</Flex>
					<TextInput
						label="Waveform name"
						value={value.waveformKey || ''}
						onChange={(event) => setField('waveformKey', event.currentTarget.value)}
					/>
					<Text weight={600}>Radio link budget</Text>
					<Flex gap="sm" direction={{ base: 'column', sm: 'row' }} align="flex-start">
						<NumberInput
							label="Transmit power (dBm)"
							rightSection={<Text size="xs">dBm</Text>}
							rightSectionWidth={UNIT_SECTION_WIDTH}
							value={value.txPowerDbm}
							min={RADIO_PARAMETER_LIMITS.txPowerDbm.min}
							max={RADIO_PARAMETER_LIMITS.txPowerDbm.max}
							precision={RADIO_PARAMETER_LIMITS.txPowerDbm.precision}
							step={RADIO_PARAMETER_LIMITS.txPowerDbm.step}
							removeTrailingZeros
							onChange={(number) =>
								setField(
									'txPowerDbm',
									typeof number === 'number' ? number : undefined
								)
							}
							w="100%"
						/>
						<NumberInput
							label="Transmit power (W)"
							rightSection={<Text size="xs">W</Text>}
							rightSectionWidth={UNIT_SECTION_WIDTH}
							value={
								typeof value.txPowerDbm === 'number'
									? dbmToWatts(value.txPowerDbm)
									: undefined
							}
							min={TRANSMIT_POWER_WATTS_LIMITS.min}
							max={TRANSMIT_POWER_WATTS_LIMITS.max}
							precision={TRANSMIT_POWER_WATTS_LIMITS.precision}
							step={TRANSMIT_POWER_WATTS_LIMITS.step}
							removeTrailingZeros
							onChange={(number) =>
								setField(
									'txPowerDbm',
									typeof number === 'number' ? wattsToDbm(number) : undefined
								)
							}
							w="100%"
						/>
					</Flex>
					{BUDGET_FIELDS.map((field) => {
						const limits = RADIO_PARAMETER_LIMITS[field];
						return (
							<NumberInput
								key={field}
								label={limits.label}
								rightSection={<Text size="xs">{limits.unit}</Text>}
								rightSectionWidth={UNIT_SECTION_WIDTH}
								value={value[field] as number | undefined}
								min={limits.min}
								max={limits.max}
								precision={limits.precision}
								step={limits.step}
								removeTrailingZeros
								onChange={(number) =>
									setField(field, typeof number === 'number' ? number : undefined)
								}
							/>
						);
					})}

					<Text weight={600}>Cesium link-quality thresholds</Text>
					<Flex gap="sm" direction={{ base: 'column', sm: 'row' }}>
						{['Unstable', 'Usable', 'Good'].map((label, index) => (
							<NumberInput
								key={label}
								label={label}
								rightSection={<Text size="xs">dB</Text>}
								rightSectionWidth={UNIT_SECTION_WIDTH}
								value={
									(value.linkQualityDisplayThresholdsDb || DEFAULT_THRESHOLDS)[
										index
									]
								}
								disabled={index === 0}
								min={0}
								max={100}
								precision={1}
								step={0.5}
								removeTrailingZeros
								onChange={(number) => setThreshold(index, number)}
								w="100%"
							/>
						))}
					</Flex>
				</>
			) : null}
		</Stack>
	);
};
